import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from '@headlessui/react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  NoSymbolIcon,
  PlusIcon,
  ShieldCheckIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  SLIDE_OVER_BACKDROP_CLASS,
  SLIDE_OVER_PANEL_TRANSITION_CLASS,
  SLIDE_OVER_TOP_OFFSET_STYLE,
} from '../../../../components/slideOverTransition';

const money = (value) => new Intl.NumberFormat('en-IE', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
}).format(Number(value) || 0);

// Payment totals are non-negative. This mirrors the backend's commercial
// cent rounding before the strict `< threshold` carry-over decision.
const roundMoney = (value) => Math.round(
  ((Number(value) || 0) + Number.EPSILON) * 100
) / 100;

/**
 * Group-level payment email workflow rendered in the shared right-side panel.
 *
 * The Payments table is intentionally read-only. Recipient selection lives
 * here, where the operator can see the complete union across the selected
 * payment group and the combined money totals that each email will contain.
 */
export default function PaymentEmailPanel({
  open,
  targets = [],
  selectedTargetKey,
  onTargetChange,
  group,
  resources = [],
  participants = [],
  payoutThreshold = null,
  siblingStatus = {},
  validationStatus = {},
  onRunValidations,
  warnings = [],
  marketOwnerCc,
  extraRecipients,
  onAddCc,
  onRemoveCc,
  onCancel,
  onConfirm,
  sending,
  monthLabel,
  year,
}) {
  const [ccInput, setCcInput] = useState('');
  const [participantFilter, setParticipantFilter] = useState('');
  const [participantSort, setParticipantSort] = useState({
    key: null, direction: 'asc',
  });
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showResendConfirmation, setShowResendConfirmation] = useState(false);
  const ccInputRef = useRef(null);

  const participantKey = (participants || [])
    .map((participant) => `${participant.user}:${participant.emailSent ? 1 : 0}`)
    .join('|');

  useEffect(() => {
    if (!open) return;
    setCcInput('');
    setParticipantFilter('');
    setParticipantSort({key: null, direction: 'asc'});
    setShowResendConfirmation(false);
  }, [open, selectedTargetKey]);

  useEffect(() => {
    if (!open) return;
    setSelectedUsers(new Set(
      (participants || [])
        .filter((participant) => !participant.emailSent)
        .map((participant) => participant.user)
    ));
    setShowResendConfirmation(false);
  }, [open, selectedTargetKey, participantKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleParticipants = useMemo(() => {
    const query = participantFilter.trim().toLowerCase();
    const filtered = !query ? [...(participants || [])] : (participants || []).filter((participant) =>
      participant.email.toLowerCase().includes(query)
      || participant.markets.some((market) => market.name.toLowerCase().includes(query))
    );
    if (!participantSort.key) return filtered;

    const valueFor = (participant) => {
      switch (participantSort.key) {
        case 'email': return participant.email.toLowerCase();
        case 'markets': return participant.markets
          .map((market) => market.name.toLowerCase()).sort().join(', ');
        case 'outstandingPayment': return Number(participant.outstandingPayment) || 0;
        case 'monthPayment': return Number(participant.monthPayment) || 0;
        case 'totalPayment': return Number(participant.totalPayment) || 0;
        default: return '';
      }
    };

    const direction = participantSort.direction === 'asc' ? 1 : -1;
    return filtered
      .map((participant, index) => ({participant, index}))
      .sort((a, b) => {
        const left = valueFor(a.participant);
        const right = valueFor(b.participant);
        const compared = typeof left === 'string'
          ? left.localeCompare(right)
          : left - right;
        return compared === 0 ? a.index - b.index : compared * direction;
      })
      .map(({participant}) => participant);
  }, [participants, participantFilter, participantSort]);

  const thresholdAvailable = payoutThreshold !== null
    && payoutThreshold !== undefined
    && Number.isFinite(Number(payoutThreshold));
  const payoutStats = useMemo(() => {
    if (!thresholdAvailable) return {paidNow: null, carriedForward: null};
    const threshold = roundMoney(payoutThreshold);
    const selectedParticipants = (participants || []).filter((participant) =>
      selectedUsers.has(participant.user));
    const carriedForward = selectedParticipants.filter((participant) => {
      const marketTotals = participant.marketTotals?.length
        ? participant.marketTotals
        : [participant.totalPayment];
      const combinedTotal = marketTotals.reduce(
        (sum, marketTotal) => sum + roundMoney(marketTotal), 0);
      return combinedTotal < threshold;
    }).length;
    return {
      paidNow: selectedParticipants.length - carriedForward,
      carriedForward,
    };
  }, [participants, payoutThreshold, selectedUsers, thresholdAvailable]);

  // An inactive member with nothing calculated can never get payments, so the
  // backend drops it from the bundle instead of blocking the send.
  const skipped = resources.filter((resource) => resource.is_active === false
    && siblingStatus[resource.id] === false);
  const missing = resources.filter((resource) => siblingStatus[resource.id] === false
    && resource.is_active !== false);
  const failed = resources.filter((resource) => siblingStatus[resource.id] === 'error');
  const unresolved = resources.filter((resource) => siblingStatus[resource.id] === undefined);
  const includedCount = resources.length - skipped.length;
  const includedResources = resources.filter((resource) =>
    !skipped.some((skippedResource) => skippedResource.id === resource.id));
  const validationFailed = includedResources.filter((resource) => {
    const report = validationStatus[resource.id];
    return report === 'error'
      || (report !== undefined && report !== 'running' && !report?.summary);
  });
  const validationNotRun = includedResources.filter((resource) =>
    validationStatus[resource.id] === undefined);
  const validationRunning = includedResources.filter((resource) =>
    validationStatus[resource.id] === 'running');
  const validationChecksFailed = includedResources.filter((resource) =>
    validationStatus[resource.id]?.summary?.fail > 0);
  const validationsPassed = includedResources.length > 0
    && validationNotRun.length === 0
    && validationRunning.length === 0
    && validationFailed.length === 0
    && validationChecksFailed.length === 0;
  const validating = validationRunning.length > 0;
  const label = group?.label || resources[0]?.name || 'Payment batch';
  const eligibleParticipants = (participants || []).filter((participant) => !participant.emailSent);
  const sentCount = (participants || []).length - eligibleParticipants.length;
  const selectedCount = selectedUsers.size;
  const selectedSentCount = (participants || []).filter((participant) =>
    participant.emailSent && selectedUsers.has(participant.user)).length;
  const blocked = missing.length > 0 || failed.length > 0 || unresolved.length > 0
    || !validationsPassed || selectedCount === 0;
  const hasErrorWarning = (warnings || []).some((warning) => warning.type === 'error');

  const marketStatus = (resource) => {
    const status = siblingStatus[resource.id];
    if (status === undefined) {
      return {label: 'Checking...', Icon: ClockIcon,
        classes: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'};
    }
    if (status === 'error') {
      return {label: 'Check failed', Icon: ExclamationTriangleIcon,
        classes: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'};
    }
    if (status === false && resource.is_active === false) {
      return {label: 'Not included', Icon: NoSymbolIcon,
        classes: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'};
    }
    if (status === true) {
      return {label: 'Calculated', Icon: CheckCircleIcon,
        classes: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'};
    }
    return {label: 'Not calculated', Icon: ExclamationTriangleIcon,
      classes: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'};
  };

  const validationMarketStatus = (resource) => {
    const report = validationStatus[resource.id];
    if (report === undefined) {
      return {label: 'Validation not run', Icon: ShieldCheckIcon,
        classes: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'};
    }
    if (report === 'running') {
      return {label: 'Validating...', Icon: ArrowPathIcon, spin: true,
        classes: 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'};
    }
    if (report === 'error') {
      return {label: 'Validation error', Icon: ExclamationTriangleIcon,
        classes: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'};
    }
    if (!report?.summary) {
      return {label: 'Invalid validation report', Icon: ExclamationTriangleIcon,
        classes: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'};
    }
    if (report?.summary?.fail > 0) {
      return {label: `${report.summary.fail} failed`, Icon: XCircleIcon,
        classes: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'};
    }
    if (report?.summary?.warn > 0) {
      return {label: `Validated · ${report.summary.warn} warning${report.summary.warn === 1 ? '' : 's'}`, Icon: CheckCircleIcon,
        classes: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'};
    }
    return {label: 'Validated', Icon: CheckCircleIcon,
      classes: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'};
  };

  const addCc = () => {
    const value = ccInput.trim();
    if (!value || !ccInputRef.current?.checkValidity()) return;
    if (!extraRecipients.includes(value)) onAddCc(value);
    setCcInput('');
  };

  const toggleParticipant = (userId) => {
    setShowResendConfirmation(false);
    setSelectedUsers((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const selectAllPending = () => {
    setShowResendConfirmation(false);
    setSelectedUsers(new Set(
      eligibleParticipants.map((participant) => participant.user)
    ));
  };

  const selectAllParticipants = () => {
    setShowResendConfirmation(false);
    setSelectedUsers(new Set(
      (participants || []).map((participant) => participant.user)
    ));
  };

  const clearParticipants = () => {
    setShowResendConfirmation(false);
    setSelectedUsers(new Set());
  };

  const requestSend = () => {
    if (selectedSentCount > 0) {
      setShowResendConfirmation(true);
      return;
    }
    onConfirm(Array.from(selectedUsers), false);
  };

  const confirmResend = () => {
    onConfirm(Array.from(selectedUsers), true);
  };

  const changeParticipantSort = (key) => {
    setParticipantSort((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      const numeric = ['outstandingPayment', 'monthPayment', 'totalPayment']
        .includes(key);
      return {key, direction: numeric ? 'desc' : 'asc'};
    });
  };

  const sortableHeader = (label, key, align = 'left') => {
    const active = participantSort.key === key;
    const SortIcon = !active
      ? ChevronUpDownIcon
      : participantSort.direction === 'asc' ? ChevronUpIcon : ChevronDownIcon;
    return (
      <th
        className={`px-3 py-2 font-medium ${align === 'right' ? 'text-right' : ''}`}
        aria-sort={!active ? 'none' : participantSort.direction === 'asc'
          ? 'ascending' : 'descending'}
      >
        <button
          type="button"
          onClick={() => changeParticipantSort(key)}
          aria-label={`Sort by ${label}`}
          className={`inline-flex items-center gap-1 rounded-sm transition-colors hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:text-white ${align === 'right' ? 'ml-auto' : ''}`}
        >
          {label}
          <SortIcon className={`h-3.5 w-3.5 ${active ? 'text-brand-600 dark:text-brand-400' : 'text-gray-400'}`} aria-hidden="true" />
        </button>
      </th>
    );
  };

  return (
    <Dialog open={open} onClose={onCancel} transition className="relative z-[60]">
      <DialogBackdrop transition className={SLIDE_OVER_BACKDROP_CLASS} />
      <div className="fixed inset-0 z-10 flex justify-end pointer-events-none" style={SLIDE_OVER_TOP_OFFSET_STYLE}>
        <DialogPanel
          as="aside"
          transition
          aria-label="Payment email batch"
          className={`pointer-events-auto relative flex h-full w-full max-w-5xl flex-col border-l border-gray-200/70 bg-surface shadow-xl dark:border-gray-600/70 dark:bg-gray-870 ${SLIDE_OVER_PANEL_TRANSITION_CLASS}`}
        >
          <header className="flex-shrink-0 border-b border-slate-900/10 px-6 py-5 dark:border-gray-100/10 sm:px-10">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-brand-600 dark:text-brand-400">
                  Payment email batch
                </p>
                <DialogTitle as="h3" className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">
                  Select recipients and send
                </DialogTitle>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  One consolidated email per participant, with amounts combined across their markets.
                </p>
              </div>
              <button
                type="button"
                onClick={onCancel}
                className="flex-shrink-0 rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                aria-label="Close payment email panel"
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </header>

          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5 sm:px-10">
        <section>
          <label htmlFor="payment-email-scope" className="text-[11px] font-medium uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
            Payment scope
          </label>
          <select
            id="payment-email-scope"
            value={selectedTargetKey || ''}
            onChange={(event) => onTargetChange(event.target.value)}
            className="mt-2 w-full rounded-md border border-gray-300 bg-transparent px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-600 dark:text-gray-100"
          >
            {(targets || []).map((target) => (
              <option key={target.key} value={target.key}>{target.label}</option>
            ))}
          </select>
          <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
            {group
              ? `${resources.length} resources in this payment group. Every selected participant receives one email.`
              : 'This resource is not assigned to a payment group.'}
          </p>
        </section>

        <div className="grid grid-cols-2 divide-x divide-y divide-slate-900/10 overflow-hidden rounded-lg border border-slate-900/10 bg-surface dark:divide-gray-100/10 dark:border-gray-700 dark:bg-gray-870 sm:grid-cols-3 lg:grid-cols-6 lg:divide-y-0">
          {[
            ['Participants', participants.length],
            ['Pending', eligibleParticipants.length],
            ['Selected', selectedCount],
            ['Paid this month', payoutStats.paidNow ?? '—'],
            ['Carried forward', payoutStats.carriedForward ?? '—'],
            ['Markets', includedCount],
          ].map(([statLabel, value]) => (
            <div key={statLabel} className="px-3 py-3 text-center">
              <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">{statLabel}</p>
              <p className="mt-1 font-mono text-lg font-light tabular-nums text-slate-900 dark:text-white">{value}</p>
            </div>
          ))}
        </div>
        <p className="-mt-3 text-xs text-gray-500 dark:text-gray-400">
          Payment outcome for the current selection based on the combined market total
          {thresholdAvailable ? ` and ${money(payoutThreshold)} payout threshold.` : '.'}
        </p>

        <section>
          <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h4 className="text-[11px] font-medium uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                Markets covered
              </h4>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Calculations must exist and validation must pass for every included market.
              </p>
            </div>
            <div className="flex flex-shrink-0 items-center gap-3">
              <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{monthLabel} {year}</span>
              <button
                type="button"
                onClick={onRunValidations}
                disabled={validating || unresolved.length > 0 || missing.length > 0 || failed.length > 0}
                className="inline-flex items-center gap-1.5 rounded-md border border-brand-500 px-3 py-1.5 text-xs font-semibold text-brand-700 transition-colors hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-45 dark:text-brand-300 dark:hover:bg-brand-900/30"
              >
                <ArrowPathIcon className={`h-4 w-4 ${validating ? 'animate-spin' : ''}`} aria-hidden="true" />
                {validating ? 'Running validations...' : validationsPassed ? 'Run again' : 'Run validations'}
              </button>
            </div>
          </div>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {resources.map((resource) => {
              const status = marketStatus(resource);
              const StatusIcon = status.Icon;
              const validation = validationMarketStatus(resource);
              const ValidationIcon = validation.Icon;
              return (
                <li key={resource.id} className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-slate-900/10 bg-surface px-3 py-2.5 dark:border-gray-700 dark:bg-gray-870">
                  <p className="truncate font-mono text-xs text-gray-800 dark:text-gray-200" title={resource.name}>{resource.name}</p>
                  <div className="flex flex-shrink-0 flex-col items-end gap-1">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${status.classes}`}>
                      <StatusIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      {status.label}
                    </span>
                    {!(resource.is_active === false && siblingStatus[resource.id] === false) && (
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${validation.classes}`}>
                        <ValidationIcon className={`h-3.5 w-3.5 ${validation.spin ? 'animate-spin' : ''}`} aria-hidden="true" />
                        {validation.label}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          {missing.length > 0 && (
            <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              Sending is blocked: {missing.map((resource) => resource.name).join(', ')} has no calculated payments for {monthLabel} {year}.
            </p>
          )}
          {failed.length > 0 && (
            <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs leading-5 text-red-700 dark:bg-red-900/30 dark:text-red-300">
              Could not check {failed.map((resource) => resource.name).join(', ')}. Close this dialog and try again.
            </p>
          )}
          {skipped.length > 0 && (
            <p className="mt-2 rounded-md bg-gray-100 px-3 py-2 text-xs leading-5 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              {skipped.map((resource) => resource.name).join(', ')} is inactive with no calculated payments and will not be included.
            </p>
          )}
          {validationNotRun.length > 0 && unresolved.length === 0 && (
            <p className="mt-2 flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" />
              Validations have not been run for this payment scope. Run them before sending emails.
            </p>
          )}
          {validationRunning.length > 0 && (
            <p className="mt-2 flex items-center gap-2 rounded-md bg-brand-50 px-3 py-2 text-xs text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
              <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
              Running payment validations for {validationRunning.length} market{validationRunning.length === 1 ? '' : 's'}...
            </p>
          )}
          {validationFailed.length > 0 && (
            <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs leading-5 text-red-700 dark:bg-red-900/30 dark:text-red-300">
              Could not validate {validationFailed.map((resource) => resource.name).join(', ')}. Sending remains blocked; run validations again.
            </p>
          )}
          {validationChecksFailed.length > 0 && (
            <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs leading-5 text-red-700 dark:bg-red-900/30 dark:text-red-300">
              Validation checks failed for {validationChecksFailed.map((resource) => resource.name).join(', ')}. Fix or recalculate the payment results, then run validations again.
            </p>
          )}
        </section>

        <section>
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h4 className="text-[11px] font-medium uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                Participants
              </h4>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Amounts are combined across all markets listed for each participant.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={selectAllPending}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                Select all pending
              </button>
              <button
                type="button"
                onClick={selectAllParticipants}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={clearParticipants}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-slate-900/10 dark:border-gray-700">
            <div className="border-b border-slate-900/10 p-3 dark:border-gray-700">
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                <input
                  type="search"
                  value={participantFilter}
                  onChange={(event) => setParticipantFilter(event.target.value)}
                  placeholder="Search participant or market"
                  aria-label="Search email participants"
                  className="w-full rounded-md border border-gray-300 bg-transparent py-2 pl-9 pr-3 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-600 dark:text-gray-200"
                />
              </div>
            </div>
            <div className="max-h-72 overflow-auto">
              <table className="w-full min-w-[760px] text-left text-xs">
                <thead className="sticky top-0 z-10 bg-surface text-[10px] uppercase tracking-[0.12em] text-gray-500 dark:bg-gray-870 dark:text-gray-400">
                  <tr className="border-b border-slate-900/10 dark:border-gray-700">
                    <th className="w-10 px-3 py-2"><span className="sr-only">Selected</span></th>
                    {sortableHeader('Participant', 'email')}
                    {sortableHeader('Markets', 'markets')}
                    {sortableHeader('Outstanding', 'outstandingPayment', 'right')}
                    {sortableHeader('Month payment', 'monthPayment', 'right')}
                    {sortableHeader('Total payment', 'totalPayment', 'right')}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-900/10 dark:divide-gray-700">
                  {visibleParticipants.map((participant) => (
                    <tr key={participant.user} className={participant.emailSent ? 'bg-amber-50/40 dark:bg-amber-900/10' : ''}>
                      <td className="px-3 py-2.5 align-top">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(participant.user)}
                          onChange={() => toggleParticipant(participant.user)}
                          aria-label={`Select ${participant.email}`}
                          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                        />
                      </td>
                      <td className="max-w-[250px] px-3 py-2.5 align-top">
                        <p className="truncate font-medium text-gray-800 dark:text-gray-100" title={participant.email}>{participant.email}</p>
                        {participant.emailSent && <p className="mt-0.5 text-[10px] font-semibold text-green-700 dark:text-green-300">Email already sent</p>}
                        {!participant.emailSent && participant.invoiceRecipient === null && <p className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">Forecaster copy disabled</p>}
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        <div className="flex max-w-[240px] flex-wrap gap-1">
                          {participant.markets.map((market) => (
                            <span key={market.id} title={market.name} className="max-w-[220px] truncate rounded-full bg-brand-50 px-2 py-0.5 text-[10px] text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
                              {market.name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-gray-700 dark:text-gray-200">{money(participant.outstandingPayment)}</td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-gray-700 dark:text-gray-200">{money(participant.monthPayment)}</td>
                      <td className="px-3 py-2.5 text-right font-mono font-semibold tabular-nums text-slate-900 dark:text-white">{money(participant.totalPayment)}</td>
                    </tr>
                  ))}
                  {visibleParticipants.length === 0 && (
                    <tr><td colSpan="6" className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">No participants match this search.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {sentCount > 0 && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {sentCount} participant{sentCount === 1 ? '' : 's'} already received this payment email. They can be selected for preview or resend; resending requires confirmation.
            </p>
          )}
        </section>

        {warnings && warnings.length > 0 && (
          <section className={`rounded-lg border px-4 py-3 ${hasErrorWarning
            ? 'border-red-200 bg-red-50 dark:border-red-900/60 dark:bg-red-900/20'
            : 'border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-900/20'}`}>
            <h4 className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${hasErrorWarning
              ? 'text-red-800 dark:text-red-300' : 'text-amber-800 dark:text-amber-300'}`}>
              Review before sending
            </h4>
            <ul className="mt-2 space-y-2 text-xs text-gray-700 dark:text-gray-200">
              {warnings.map((warning, index) => (
                <li key={index} className="flex items-start gap-2 leading-5">
                  <ExclamationTriangleIcon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${warning.type === 'error'
                    ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                  <div><p className="font-semibold">{warning.label}</p>{warning.description && <p className="text-gray-600 dark:text-gray-300">{warning.description}</p>}</div>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section>
          <div className="mb-2">
            <h4 className="text-[11px] font-medium uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Copy recipients</h4>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">These addresses receive a copy of every selected email.</p>
          </div>
          <div className="rounded-lg border border-slate-900/10 bg-surface p-3 dark:border-gray-700 dark:bg-gray-870">
            <div className="flex flex-wrap items-center gap-2">
              {marketOwnerCc && <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">{marketOwnerCc} (market owner)</span>}
              {extraRecipients.map((email) => (
                <span key={email} className="inline-flex items-center rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-700 dark:border-gray-600 dark:text-gray-200">
                  {email}
                  <button type="button" className="ml-1 rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:text-gray-200" onClick={() => onRemoveCc(email)} aria-label={`remove ${email}`}>
                    <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input ref={ccInputRef} type="email" value={ccInput} placeholder="name@example.com" aria-label="add CC for this send" className="min-w-0 flex-1 rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-600 dark:text-gray-200" onChange={(event) => setCcInput(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addCc(); } }} />
              <button type="button" onClick={addCc} disabled={!ccInput.trim()} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-45 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600">
                <PlusIcon className="h-4 w-4" aria-hidden="true" /> Add
              </button>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-slate-900/10 bg-surface px-4 py-3 dark:border-gray-700 dark:bg-gray-870">
          <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">Subject</p>
          <p className="mt-1 break-words font-mono text-xs leading-5 text-gray-700 dark:text-gray-200">[Predico] Payment details for {monthLabel} {year} - {label}</p>
        </section>
      </div>

      {showResendConfirmation ? (
        <div className="flex-shrink-0 border-t border-amber-200 bg-amber-50 px-6 py-4 dark:border-amber-900/60 dark:bg-amber-900/20 sm:px-10">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex min-w-0 items-start gap-3">
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Resend payment emails?</p>
                <p className="mt-0.5 text-xs leading-5 text-amber-800 dark:text-amber-300">
                  {selectedSentCount} of the {selectedCount} selected forecasters already received this payment email. Continuing will send them another copy.
                </p>
              </div>
            </div>
            <div className="flex gap-2 sm:ml-auto">
              <button type="button" onClick={() => setShowResendConfirmation(false)} className="flex-1 rounded-md border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 transition-colors hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:border-amber-700 dark:bg-transparent dark:text-amber-200 dark:hover:bg-amber-900/30 sm:flex-none">Go back</button>
              <button type="button" onClick={confirmResend} disabled={sending} className="flex-1 rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:cursor-not-allowed disabled:opacity-45 sm:flex-none">
                {sending ? 'Sending...' : `Send again to ${selectedCount}`}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-shrink-0 flex-col gap-3 border-t border-slate-900/10 px-6 py-4 dark:border-gray-100/10 sm:flex-row sm:items-center sm:px-10">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">
              {blocked ? 'Review required' : `${selectedCount} email${selectedCount === 1 ? '' : 's'} ready`}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">One consolidated email per selected participant.</p>
          </div>
          <div className="flex gap-2 sm:ml-auto">
            <button type="button" onClick={onCancel} className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600 sm:flex-none">Cancel</button>
            <button type="button" onClick={requestSend} disabled={blocked || sending} className="flex-1 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-45 sm:flex-none">
              {sending ? 'Sending...' : `Send ${selectedCount} email${selectedCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}
